import React, { useState, useEffect, useRef } from 'react';
import { Transaction, TransactionAttachment, Category } from '../../types/budget';
import { attachmentApi } from '../../services/budgetApi';
import { Button } from '../ui/Button';
import styles from './TransactionEdit.module.css';

interface TransactionEditProps {
  transaction: Transaction;
  categories: Category[];
  onSave: (updates: {
    date: string;
    description: string;
    amount: number;
    categoryId: string | null;
  }) => void;
  onCancel: () => void;
  onDelete: () => void;
}

/**
 * Format date for input field (YYYY-MM-DD)
 * Handles both ISO strings from backend (e.g., "2024-12-30T00:00:00.000Z")
 * and simple date strings (e.g., "2024-12-30")
 */
const formatDateForInput = (dateStr: string): string => {
  // If already in YYYY-MM-DD format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  // Otherwise parse as ISO string and extract date part
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    // Fallback to today if invalid
    return new Date().toISOString().split('T')[0];
  }
  return date.toISOString().split('T')[0];
};

/**
 * Format file size for display
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const TransactionEdit: React.FC<TransactionEditProps> = ({
  transaction,
  categories,
  onSave,
  onCancel,
  onDelete,
}) => {
  const [date, setDate] = useState(formatDateForInput(transaction.date));
  const [description, setDescription] = useState(transaction.description);
  const [amount, setAmount] = useState(transaction.amount.toString());
  const [categoryId, setCategoryId] = useState<string | null>(transaction.categoryId);
  const [attachments, setAttachments] = useState<TransactionAttachment[]>([]);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(true);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]); // filenames being uploaded
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Focus description input on mount
  useEffect(() => {
    descriptionInputRef.current?.focus();
  }, []);

  // Load existing attachments
  useEffect(() => {
    const loadAttachments = async () => {
      try {
        setIsLoadingAttachments(true);
        const existingAttachments = await attachmentApi.getForTransaction(transaction.id);
        setAttachments(existingAttachments);
      } catch (error) {
        console.error('Failed to load attachments:', error);
        // Don't show error feedback for loading - just show empty state
      } finally {
        setIsLoadingAttachments(false);
      }
    };

    loadAttachments();
  }, [transaction.id]);

  // Group categories by type
  const expenseCategories = categories.filter(c => c.type === 'expense');
  const incomeCategories = categories.filter(c => c.type === 'income');
  const transferCategories = categories.filter(c => c.type === 'transfer');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) {
      setFeedback({ type: 'error', message: 'Invalid amount' });
      return;
    }

    if (!date) {
      setFeedback({ type: 'error', message: 'Date is required' });
      return;
    }

    if (!description.trim()) {
      setFeedback({ type: 'error', message: 'Description is required' });
      return;
    }

    try {
      setIsSubmitting(true);
      setFeedback(null);

      await onSave({
        date,
        description: description.trim(),
        amount: parsedAmount,
        categoryId,
      });

      setFeedback({ type: 'success', message: 'Transaction updated' });
      setTimeout(() => setFeedback(null), 2000);
    } catch (error) {
      setFeedback({ type: 'error', message: 'Failed to update transaction' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        setIsDeleting(true);
        setFeedback(null);
        await onDelete();
        setFeedback({ type: 'success', message: 'Transaction deleted' });
      } catch (error) {
        setFeedback({ type: 'error', message: 'Failed to delete transaction' });
        setIsDeleting(false);
      }
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Process each file
    for (const file of Array.from(files)) {
      // Validate file type (images and PDFs)
      const isImage = file.type.startsWith('image/');
      const isPdf = file.type === 'application/pdf';
      if (!isImage && !isPdf) {
        setFeedback({ type: 'error', message: `${file.name}: Please select an image or PDF file` });
        continue;
      }

      // Validate file size (max 10MB for S3)
      if (file.size > 10 * 1024 * 1024) {
        setFeedback({ type: 'error', message: `${file.name}: File must be smaller than 10MB` });
        continue;
      }

      try {
        // Add to uploading state
        setUploadingFiles(prev => [...prev, file.name]);
        setFeedback(null);

        // Upload to S3 and create attachment record
        const newAttachment = await attachmentApi.uploadAndCreate(transaction.id, file);
        
        // Add to attachments list
        setAttachments(prev => [...prev, newAttachment]);
      } catch (error) {
        console.error('Failed to upload file:', error);
        setFeedback({ type: 'error', message: `Failed to upload ${file.name}` });
      } finally {
        // Remove from uploading state
        setUploadingFiles(prev => prev.filter(name => name !== file.name));
      }
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = async (attachment: TransactionAttachment) => {
    try {
      await attachmentApi.delete(attachment.id);
      setAttachments(prev => prev.filter(a => a.id !== attachment.id));
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      setFeedback({ type: 'error', message: 'Failed to remove attachment' });
    }
  };

  const handleViewAttachment = (attachment: TransactionAttachment) => {
    // Open the attachment in a new tab using the redirect URL pattern
    const viewUrl = attachmentApi.getViewUrl(attachment.s3Key);
    window.open(viewUrl, '_blank');
  };

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmit}>
        {/* Date and Amount row */}
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="transaction-date" className={styles.label}>Date</label>
            <input
              id="transaction-date"
              type="date"
              className={styles.input}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="transaction-amount" className={styles.label}>Amount</label>
            <input
              id="transaction-amount"
              type="number"
              step="0.01"
              className={styles.input}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>
        </div>

        {/* Description row */}
        <div className={styles.field}>
          <label htmlFor="transaction-description" className={styles.label}>Description</label>
          <input
            ref={descriptionInputRef}
            id="transaction-description"
            type="text"
            className={styles.input}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Transaction description"
            required
          />
          {transaction.originalDescription !== transaction.description && (
            <span className={styles.originalDescription} title="Original description">
              Original: {transaction.originalDescription}
            </span>
          )}
        </div>

        {/* Category row */}
        <div className={styles.field}>
          <label htmlFor="transaction-category" className={styles.label}>Category</label>
          <select
            id="transaction-category"
            className={styles.select}
            value={categoryId || ''}
            onChange={(e) => setCategoryId(e.target.value || null)}
          >
            <option value="">Uncategorized</option>
            {expenseCategories.length > 0 && (
              <optgroup label="Expenses">
                {expenseCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </optgroup>
            )}
            {incomeCategories.length > 0 && (
              <optgroup label="Income">
                {incomeCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </optgroup>
            )}
            {transferCategories.length > 0 && (
              <optgroup label="Transfers">
                {transferCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {/* Attachments section */}
        <div className={styles.field}>
          <label className={styles.label}>Receipts/Invoices</label>
          <div className={styles.attachmentsSection}>
            {/* Existing attachments grid */}
            {isLoadingAttachments ? (
              <div className={styles.attachmentsLoading}>Loading attachments...</div>
            ) : attachments.length > 0 ? (
              <div className={styles.attachmentsGrid}>
                {attachments.map((attachment) => {
                  const isPdf = attachment.contentType === 'application/pdf';
                  return (
                    <div key={attachment.id} className={styles.attachmentItem}>
                      {isPdf ? (
                        <div
                          className={styles.pdfThumbnail}
                          onClick={() => handleViewAttachment(attachment)}
                        >
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9 15H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9 11H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span className={styles.pdfLabel}>PDF</span>
                        </div>
                      ) : (
                        <img
                          src={attachmentApi.getViewUrl(attachment.s3Key)}
                          alt={attachment.filename}
                          className={styles.attachmentThumbnail}
                          onClick={() => handleViewAttachment(attachment)}
                        />
                      )}
                      <div className={styles.attachmentOverlay}>
                        <button
                          type="button"
                          className={styles.attachmentDeleteBtn}
                          onClick={() => handleRemoveAttachment(attachment)}
                          aria-label={`Remove ${attachment.filename}`}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 4L12 12M4 12L12 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                      <div className={styles.attachmentInfo}>
                        <span className={styles.attachmentFilename} title={attachment.filename}>
                          {attachment.filename.length > 15
                            ? attachment.filename.slice(0, 12) + '...'
                            : attachment.filename}
                        </span>
                        <span className={styles.attachmentSize}>
                          {formatFileSize(attachment.size)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {/* Uploading indicators */}
            {uploadingFiles.length > 0 && (
              <div className={styles.uploadingIndicators}>
                {uploadingFiles.map((filename) => (
                  <div key={filename} className={styles.uploadingItem}>
                    <div className={styles.uploadingSpinner} />
                    <span>Uploading {filename}...</span>
                  </div>
                ))}
              </div>
            )}

            {/* Upload button */}
            <div className={styles.attachmentUpload}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,application/pdf"
                multiple
                className={styles.fileInput}
                onChange={handleFileChange}
                id="attachment-input"
              />
              <label htmlFor="attachment-input" className={styles.uploadLabel}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 4V20M4 12H20"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                {attachments.length > 0 ? 'Add More' : 'Add Receipt'}
              </label>
            </div>
          </div>
        </div>

        {/* Feedback */}
        {feedback && (
          <div className={feedback.type === 'success' ? styles.successFeedback : styles.errorFeedback}>
            {feedback.message}
          </div>
        )}

        {/* Action buttons */}
        <div className={styles.buttonRow}>
          <Button
            variant="danger"
            size="sm"
            onClick={handleDelete}
            disabled={isDeleting}
            loading={isDeleting}
            leftIcon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.5 7V12H6.5V7H5.5ZM9.5 7V12H10.5V7H9.5Z" fill="currentColor"/>
                <path d="M2 3V4H3V14C3 14.2652 3.10536 14.5196 3.29289 14.7071C3.48043 14.8946 3.73478 15 4 15H12C12.2652 15 12.5196 14.8946 12.7071 14.7071C12.8946 14.5196 13 14.2652 13 14V4H14V3H2ZM4 14V4H12V14H4Z" fill="currentColor"/>
                <path d="M6 1H10V2H6V1Z" fill="currentColor"/>
              </svg>
            }
          >
            Delete
          </Button>

          <div className={styles.actionButtons}>
            <Button
              variant="secondary"
              size="sm"
              onClick={onCancel}
              disabled={isSubmitting || isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={isSubmitting || isDeleting}
              loading={isSubmitting}
            >
              Save
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TransactionEdit;
